import { useId, useState } from 'react';
import type { PricingLevel, Service, ServiceLevelPrice, ServiceVariant } from '@salonomy/types';
import { Button, Field, Modal, Pill, Spinner, TextInput } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import {
  useCreateServiceVariant,
  useDeleteServiceLevelPrice,
  useDeleteServiceVariant,
  useServicePricingMatrix,
  useSetServiceLevelPrice,
  useUpdateServiceVariant,
} from '../../data/pricing.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from './poundsPence.js';

/**
 * The per-service variant × level pricing matrix (WS9 brief 09 §1). A service grows VARIANTS (Cut →
 * short/medium/long), each with its own duration + base price, and the salon's pricing LEVELS become
 * the grid columns. Most cells take their price from the level's % adjustment over the variant's base
 * (shown as derived, dimmed); set an absolute OVERRIDE in a cell to pin a flat price for that exact
 * (variant, level) combination — the override wins.
 *
 * Honesty: no price is computed here. Variant base prices and absolute overrides are the only money
 * the manager types (pounds↔pence at the edge); the derived per-cell figure and the service's
 * "from £X" come straight from the API's matrix (`priceFromPence`) — the same numbers booking
 * surfaces resolve and the till charges (shown == charged).
 *
 * The grid is intentionally read-leaning: variants are edited as rows, overrides as cell actions —
 * we don't invent a spreadsheet interaction the (not-yet-designed) checkout/pricing visual language
 * doesn't yet cover. See the integrator note in the WS9 summary.
 */
export function ServicePricingMatrixModal({
  service,
  onClose,
}: {
  service: Service;
  onClose: () => void;
}) {
  const { data: matrix, isLoading } = useServicePricingMatrix(service.id);

  const variants = matrix?.variants.filter((v) => v.active) ?? [];
  const levels = matrix?.levels.filter((l) => l.active) ?? [];

  const [variantModal, setVariantModal] = useState<{ existing?: ServiceVariant } | null>(null);
  const [cellModal, setCellModal] = useState<{
    variant: ServiceVariant | null;
    level: PricingLevel;
  } | null>(null);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={`Pricing — ${service.name}`}
        width={760}
        footer={
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        }
      >
        {isLoading || !matrix ? (
          <div
            className="muted"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '20px 0' }}
          >
            <Spinner /> Loading pricing…
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div className="muted" style={{ fontSize: 13 }}>
                Shows from <strong className="mono">{money(matrix.priceFromPence)}</strong> — the
                lowest bookable price across variants and levels. This is the “from £X” clients see.
              </div>
              {levels.length === 0 ? (
                <Pill tone="soft" className="mono">
                  No levels set
                </Pill>
              ) : null}
            </div>

            {/* ── Variants ── */}
            <div className="section-title" style={{ fontSize: 15, margin: '14px 0 2px' }}>
              Variants
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
              Lengths or options of this service, each with its own time and base price (the price
              before any level adjustment). With no variants, the service’s own price is the base.
            </div>
            <div className="settings-list">
              {variants.length === 0 ? (
                <div className="muted" style={{ fontSize: 13, padding: '4px 0' }}>
                  No variants — bookings use the service’s single price.
                </div>
              ) : (
                variants.map((v) => (
                  <div key={v.id} className="settings-row">
                    <div>
                      <div style={{ fontWeight: 600 }}>{v.name}</div>
                      <div className="muted" style={{ fontSize: 12.5 }}>
                        {v.durationMin} min
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="mono">{money(v.pricePence)}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setVariantModal({ existing: v })}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              <Button size="sm" variant="outline" onClick={() => setVariantModal({})}>
                Add variant
              </Button>
            </div>

            {/* ── The variant × level grid ── */}
            <div className="section-title" style={{ fontSize: 15, margin: '18px 0 2px' }}>
              Price by level
            </div>
            {levels.length === 0 ? (
              <div className="muted" style={{ fontSize: 13, padding: '4px 0' }}>
                No pricing levels yet. Add levels in Settings → Pricing levels to charge different
                rates by seniority; this grid then lets you pin a flat price per combination.
              </div>
            ) : (
              <MatrixGrid
                variants={variants}
                levels={levels}
                overrides={matrix.overrides}
                onEditCell={(variant, level) => setCellModal({ variant, level })}
              />
            )}
          </>
        )}
      </Modal>

      {variantModal ? (
        <VariantModal
          serviceId={service.id}
          existing={variantModal.existing}
          nextSortOrder={variants.length}
          onClose={() => setVariantModal(null)}
        />
      ) : null}

      {cellModal ? (
        <LevelPriceModal
          serviceId={service.id}
          variant={cellModal.variant}
          level={cellModal.level}
          existing={findOverride(
            matrix?.overrides,
            cellModal.variant?.id ?? null,
            cellModal.level.id,
          )}
          onClose={() => setCellModal(null)}
        />
      ) : null}
    </>
  );
}

/**
 * The grid: rows are the bare service plus each variant, columns are the salon's levels. A cell shows
 * an absolute override (a pinned flat price, badged) where one exists, otherwise "from level %" as a
 * dimmed hint that the price is DERIVED by the API — we never spell the derived number out, because
 * we don't recompute it on the client. Tap a cell to pin or clear an override.
 */
function MatrixGrid({
  variants,
  levels,
  overrides,
  onEditCell,
}: {
  variants: ServiceVariant[];
  levels: PricingLevel[];
  overrides: ServiceLevelPrice[];
  onEditCell: (variant: ServiceVariant | null, level: PricingLevel) => void;
}) {
  // Row "the bare service" (variantId null) first, then each variant.
  const rows: Array<{ key: string; label: string; variant: ServiceVariant | null }> = [
    { key: 'base', label: 'Base service', variant: null },
    ...variants.map((v) => ({ key: v.id, label: v.name, variant: v })),
  ];

  return (
    <div style={{ overflowX: 'auto', marginTop: 4 }}>
      <table className="slm-matrix">
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }} />
            {levels.map((l) => (
              <th key={l.id} className="mono" style={{ textAlign: 'center', fontWeight: 600 }}>
                {l.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row" style={{ textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {row.label}
              </th>
              {levels.map((l) => {
                const override = findOverride(overrides, row.variant?.id ?? null, l.id);
                return (
                  <td key={l.id} style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="slm-matrix__cell"
                      onClick={() => onEditCell(row.variant, l)}
                      title={
                        override
                          ? 'Flat price set — tap to change or clear'
                          : 'Derived from this level — tap to pin a flat price'
                      }
                    >
                      {override ? (
                        <span className="mono" style={{ fontWeight: 600 }}>
                          {money(override.pricePence)}
                        </span>
                      ) : (
                        <span className="muted mono" style={{ fontSize: 12 }}>
                          from level
                        </span>
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        “from level” means the price follows that level’s % adjustment. Tap a cell to pin a flat
        price for that exact combination instead.
      </div>
    </div>
  );
}

/** Create / edit one variant. Money is pounds in the input, pence over the wire (edge conversion). */
function VariantModal({
  serviceId,
  existing,
  nextSortOrder,
  onClose,
}: {
  serviceId: string;
  existing?: ServiceVariant;
  /** Where a newly-added variant sorts — the end of the current list. */
  nextSortOrder: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateServiceVariant(serviceId);
  const update = useUpdateServiceVariant(serviceId);
  const remove = useDeleteServiceVariant(serviceId);
  const labelId = useId();
  const editing = Boolean(existing);

  const [name, setName] = useState(existing?.name ?? '');
  const [durationMin, setDurationMin] = useState(String(existing?.durationMin ?? 30));
  const [price, setPrice] = useState(penceToPounds(existing?.pricePence ?? null));
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function submit() {
    if (!name.trim()) {
      toast.error('Give the variant a name.');
      return;
    }
    const duration = Number(durationMin);
    if (!Number.isInteger(duration) || duration < 5 || duration > 600) {
      toast.error('Duration must be 5–600 minutes.');
      return;
    }
    const pricePence = poundsToPence(price);
    if (pricePence == null) {
      toast.error('Enter a valid price.');
      return;
    }
    setBusy(true);
    try {
      if (editing && existing) {
        await update.mutateAsync({
          variantId: existing.id,
          body: { name: name.trim(), durationMin: duration, pricePence },
        });
        toast.success('Variant updated');
      } else {
        await create.mutateAsync({
          name: name.trim(),
          durationMin: duration,
          pricePence,
          sortOrder: nextSortOrder,
        });
        toast.success('Variant added');
      }
      onClose();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not save this variant.'));
    } finally {
      setBusy(false);
    }
  }

  async function deleteVariant() {
    if (!existing) return;
    setBusy(true);
    try {
      await remove.mutateAsync(existing.id);
      toast.success(`${existing.name} removed`);
      setConfirmRemove(false);
      onClose();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not remove this variant.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open={!confirmRemove}
        onClose={onClose}
        title={editing ? 'Edit variant' : 'Add variant'}
        width={460}
        footer={
          <>
            {editing ? (
              <Button
                variant="ghost"
                onClick={() => setConfirmRemove(true)}
                disabled={busy}
                style={{ marginRight: 'auto', color: 'var(--slm-danger)' }}
              >
                Remove
              </Button>
            ) : null}
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy}>
              {editing ? 'Save' : 'Add variant'}
            </Button>
          </>
        }
      >
        <Field label="Name" htmlFor={`${labelId}-name`}>
          <TextInput
            id={`${labelId}-name`}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Long"
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <Field label="Duration (min)" hint="Chair time" htmlFor={`${labelId}-dur`}>
            <TextInput
              id={`${labelId}-dur`}
              type="number"
              inputMode="numeric"
              min={5}
              max={600}
              value={durationMin}
              onChange={(e) => setDurationMin(e.target.value)}
            />
          </Field>
          <Field
            label="Base price (£)"
            hint="Before any level adjustment"
            htmlFor={`${labelId}-price`}
          >
            <TextInput
              id={`${labelId}-price`}
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
            />
          </Field>
        </div>
      </Modal>

      <ConfirmModal
        open={confirmRemove}
        title="Remove variant"
        confirmLabel="Remove variant"
        confirmPhrase={existing?.name}
        busy={busy}
        onConfirm={deleteVariant}
        onClose={() => setConfirmRemove(false)}
      >
        <p style={{ margin: 0 }}>
          Removing <strong>{existing?.name}</strong> stops it being bookable and clears any flat
          prices set for it. Past appointments that used it are unaffected.
        </p>
      </ConfirmModal>
    </>
  );
}

/**
 * Set or clear an absolute price for one (variant?, level) cell. Leaving the price blank and saving
 * clears the override (the cell reverts to the level’s % adjustment); an existing override can also
 * be cleared outright.
 */
function LevelPriceModal({
  serviceId,
  variant,
  level,
  existing,
  onClose,
}: {
  serviceId: string;
  variant: ServiceVariant | null;
  level: PricingLevel;
  existing: ServiceLevelPrice | undefined;
  onClose: () => void;
}) {
  const toast = useToast();
  const setPrice = useSetServiceLevelPrice(serviceId);
  const clearPrice = useDeleteServiceLevelPrice(serviceId);
  const labelId = useId();

  const [price, setPriceInput] = useState(penceToPounds(existing?.pricePence ?? null));
  const [busy, setBusy] = useState(false);

  const rowLabel = variant ? variant.name : 'the base service';

  async function save() {
    const pricePence = poundsToPence(price);
    // Empty input on a cell with an existing override means "clear it". Empty with no override is a
    // no-op (the cell already follows the level), so we just close.
    if (pricePence == null) {
      if (existing) {
        await clear();
      } else {
        onClose();
      }
      return;
    }
    setBusy(true);
    try {
      await setPrice.mutateAsync({
        levelId: level.id,
        variantId: variant?.id ?? null,
        pricePence,
      });
      toast.success(`Flat price set for ${level.name} · ${rowLabel}`);
      onClose();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not set this price.'));
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    if (!existing) return;
    setBusy(true);
    try {
      await clearPrice.mutateAsync(existing.id);
      toast.success(`Flat price cleared — ${level.name} · ${rowLabel} follows the level`);
      onClose();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not clear this price.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${level.name} · ${variant ? variant.name : 'Base service'}`}
      width={420}
      footer={
        <>
          {existing ? (
            <Button
              variant="ghost"
              onClick={clear}
              disabled={busy}
              style={{ marginRight: 'auto', color: 'var(--slm-danger)' }}
            >
              Clear flat price
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            Save
          </Button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Pin a flat price for {rowLabel} at the <strong>{level.name}</strong> level. This wins over
        the level’s % adjustment. Leave it blank to follow the level instead.
      </div>
      <Field label="Flat price (£)" htmlFor={`${labelId}-price`}>
        <TextInput
          id={`${labelId}-price`}
          autoFocus
          inputMode="decimal"
          value={price}
          onChange={(e) => setPriceInput(e.target.value)}
          placeholder="Follows the level"
        />
      </Field>
    </Modal>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Find the override for a (variantId|null, levelId) cell, if one is set. */
function findOverride(
  overrides: ServiceLevelPrice[] | undefined,
  variantId: string | null,
  levelId: string,
): ServiceLevelPrice | undefined {
  return overrides?.find((o) => o.levelId === levelId && (o.variantId ?? null) === variantId);
}

/** Surface the server's own 4xx message (422/409 etc.) faithfully; fall back for anything else. */
function apiMessage(err: unknown, fallback: string): string {
  return err instanceof ApiClientError ? err.message : fallback;
}
